import { yupResolver } from '@hookform/resolvers/yup';
import { Card, Flex } from '@mantine/core';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { CashAccountModel } from 'api-hooks/cash-account';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { ApiError, ApiResult } from 'common/repositories/common.model';
import { useAuthorization } from 'hooks/use-authorization';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import Input from 'mantine-components/elements';
import { HalfGridContainer } from 'mantine-components/container/grid-container';
import Button from 'mantine-components/widget/button';
import Text from 'mantine-components/widget/text';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UniskaiFormHeaderRef } from 'templates/form/uniskai-form-header';
import UniskaiFormTemplate from 'templates/form/uniskai-form-template';

import { CashAccountFormSchema, CashAccountFormType } from './form-type';
import useCashAccountDefaultValues from '../hooks/use-cash-account-default-values';

interface CashAccountFormProps {
  query?: UseQueryResult<ApiResult<CashAccountModel>, ApiError>;
  onSubmit: (
    input: CashAccountFormType,
    form: UseFormReturn<CashAccountFormType, any>,
  ) => Promise<CashAccountModel>;
  saveMutation?: UseMutationResult;
}

export default function CashAccountForm(props: CashAccountFormProps) {
  const { t } = useTranslation();
  const cashAccount = props.query?.data?.data;
  const defaultValues = useCashAccountDefaultValues(cashAccount);
  const titleRef = React.useRef<UniskaiFormHeaderRef>(null);

  React.useEffect(() => {
    let title = t(`pathname:${NavigationRoutes.CashAccountsCreate}`);
    if (cashAccount) {
      title = t(`pathname:${NavigationRoutes.CashAccountsView}`, {
        extra: cashAccount.code,
      });
    }
    titleRef.current?.setTitle(title);
  }, [cashAccount, t]);

  const permission = useGetCrudPermission('cash-accounts');
  const { deleteMutate, onDelete } = useDestroyAction('cash-accounts');
  const { can } = useAuthorization();
  const { navigate } = useNavigation();

  return (
    <UniskaiFormTemplate
      formProps={{
        defaultEditable: !props.query,
        defaultValues,
        onSubmit: props.onSubmit as any,
        resolver: yupResolver(CashAccountFormSchema()),
      }}
      query={props.query}
      titleRef={titleRef}
      data={cashAccount}
      saveMutation={props.saveMutation}
      customs={[
        {
          type: 'edit',
          show: permission.update,
        },
        {
          type: 'delete',
          loading: deleteMutate.isLoading,
          onClick: () => onDelete(cashAccount?.id),
          show: permission.delete,
        },
      ]}
    >
      <HalfGridContainer>
        <Input
          type="text"
          name="code"
          label={t('common:code')}
          placeholder={t('common:code')}
          required
        />
        <Input
          type="text"
          name="name"
          label={t('common:name')}
          placeholder={t('common:name')}
          required
        />
        <Input
          type="textarea"
          name="description"
          label={t('common:description')}
          placeholder={t('common:description')}
          required={false}
        />
      </HalfGridContainer>
      {cashAccount && (
        <Card withBorder radius="md" mt={16}>
          <Flex justify="space-between" align="center">
            <Text variant="body2Semibold">{t('common:balance')}</Text>
            {can(AuthorizationRules.ReportsCashMutation) && (
              <Button
                variant="subtle"
                onClick={() => {
                  navigate(NavigationRoutes.CashMutationReport, {
                    filter: {
                      cash_account: cashAccount.id,
                    },
                  });
                }}
              >
                {t('common:view_mutation')}
              </Button>
            )}
          </Flex>
          <Input
            type="number"
            name="balance"
            placeholder={t('common:balance')}
            disabled
          />
        </Card>
      )}
    </UniskaiFormTemplate>
  );
}
